import type { LocalizationService } from "../localization/LocalizationService";
import type { IntroSplashPort } from "./IntroSplashPort";
import type { IntroSplashCardIcon, IntroSplashSpec } from "./IntroSplashSpec";

/**
 * Drives the branded intro splash from the game's spec: the volatility pips, the headline-badge
 * gate, the age badge, the version label and the cards — localized here, their icons resolved to
 * image URLs by the injected resolver (the shared bundle's sheet frames, as data URLs).
 */
export class BrandedIntroSplashPresenter {
  public constructor(
    private readonly panel: IntroSplashPort,
    private readonly spec: IntroSplashSpec,
    private readonly gameVersion: string,
    /**
     * Minimum legal gambling age for the player's jurisdiction (e.g., 18 /
     * 19 / 21). `null` / non-positive / non-finite → age badge is hidden.
     */
    private readonly minAgeYears: number | null,
    private readonly localization: LocalizationService,
    private readonly resolveCardIcon: (icon: IntroSplashCardIcon) => Promise<string | null>,
  ) {}

  public async applySpec(): Promise<void> {
    this.panel.setVolatility(this.spec.volatilityRating);
    this.panel.setHeadlineBadgeShown(this.spec.headlineBadgeAssetPath !== null);
    this.applyAgeBadge();
    this.panel.setVersionText(`v${this.gameVersion}`);
    const cards = await Promise.all(
      this.spec.cards.map(async (card) => ({
        title: this.localization.t(card.titleKey, card.titleFallback),
        body: this.localization.t(card.bodyKey, card.bodyFallback),
        iconUrl: card.icon ? await this.resolveCardIcon(card.icon) : null,
      })),
    );
    this.panel.setCards(cards);
  }

  private applyAgeBadge(): void {
    const years = this.minAgeYears;
    const valid = years !== null && Number.isFinite(years) && years > 0;
    this.panel.setAgeBadge(valid ? Math.floor(years) : null);
  }
}
